import * as amplitude from "@amplitude/analytics-browser";
import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { postConversationTag } from "@redotech/redo-merchant-app-common/client/conversation-tags";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  ConversationTagWithId,
  MinimalConversation,
} from "@redotech/redo-model/conversation";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { PillTheme } from "@redotech/redo-model/pill-theme";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoList,
  RedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import PlusIcon from "@redotech/redo-web/icon-old/plus.svg";
import { ProgressBar } from "@redotech/redo-web/progress-bar";
import { TextInput } from "@redotech/redo-web/text-input";
import { memo, useEffect, useMemo, useState } from "react";
import {
  bulkAddTags,
  bulkRemoveTags,
  updateConversation,
} from "../client/conversations";
import { CreateTagModal } from "../conversation-tags/create-tag-modal";
import { createGroupedTagListItems } from "../conversation-tags/tag-grouping-utils";
import { ConversationTagsContext } from "../services/conversation-tags-service";
import { getNextBatch } from "../utils";
import * as tagsModalCss from "./tags-modal.module.css";

export const TagsModal = memo(function TagsModal({
  open,
  setOpen,
  conversations,
  mode = "add",
  cleanupFn,
  selectAllInfo,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  conversations: MinimalConversation[];
  mode: "add" | "remove";
  cleanupFn?: (clearSelected?: boolean) => void;
  selectAllInfo?: {
    count: number;
    deselectedConversations: MinimalConversation[];
    bulkActionIterator: AsyncIterator<{ conversations: MinimalConversation[] }>;
    setBlockRefresh: (val: boolean) => void;
  };
}) {
  const client = useRequiredContext(RedoMerchantClientContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const [createTagModalOpen, setCreateTagModalOpen] = useState(false);

  const team = useRequiredContext(TeamContext);

  const tagGroupsResult = useLoad(async () => {
    const response = await rpcClient.getTagGroups({});
    return response.tagGroups;
  }, [rpcClient]);

  const [conversationTagsContext, reloadConversationTags] = useLazyContext(
    ConversationTagsContext,
  );

  const conversationTags = useMemo(
    () => conversationTagsContext.value || [],
    [conversationTagsContext],
  );

  const canCreateTags = useHasPermission(SupportPermissions.MANAGE_TAG);
  const [updatingSelectAll, setUpdatingSelectAll] = useState(false);
  const [numDone, setNumDone] = useState(0);
  const conversationId =
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    conversations.length === 1 ? conversations[0]._id : null;
  const conversationTagIds = useMemo(() => {
    if (conversations.length === 1) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      return conversations[0].tagIds?.map((tag) => tag.tagId) || [];
    }
    return [];
  }, [conversations]);

  const selectedTagsLoad = useLoad(async () => {
    if (!conversationId || conversationTagIds.length === 0) {
      return [];
    }

    const tags = await rpcClient.getConversationTagsById({
      tagIds: conversationTagIds,
    });
    return tags.map((tag) => ({
      tagId: tag._id,
      name: tag.name,
      pillTheme: tag.pillTheme,
      source: tag.source,
      tagGroupId: tag.tagGroupId,
    }));
  }, [conversationId, JSON.stringify(conversationTagIds.sort()), rpcClient]);

  const [selectedTags, setSelectedTags] = useState<ConversationTagWithId[]>([]);

  useEffect(() => {
    if (selectedTagsLoad.value) {
      setSelectedTags(selectedTagsLoad.value);
    }
  }, [selectedTagsLoad.value]);

  const [searchText, setSearchText] = useState<string>("");
  const tagsBeingShown = useMemo(() => {
    if (!searchText) {
      return conversationTags;
    }
    const lower = searchText.toLowerCase();
    return conversationTags.filter((tag) =>
      tag.name.toLowerCase().includes(lower),
    );
  }, [conversationTags, searchText]);

  const [listAnchorRef, setListAnchorRef] = useState<HTMLElement | null>(null);

  const tagListItems: RedoListItem<ConversationTagWithId>[] = useMemo(() => {
    return createGroupedTagListItems(tagsBeingShown, tagGroupsResult.value, {
      includeCreateValue: false,
    });
  }, [tagsBeingShown, tagGroupsResult.value]);

  const selectedTagIds: string[] = useMemo(() => {
    return selectedTags.map((tag) => tag.tagId);
  }, [selectedTags]);

  const [hasChanged, setHasChanged] = useState(false);

  const handleConfirm = async () => {
    if (conversations.length === 1) {
      if (mode === "add") {
        // Add tags
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        await updateConversation(client, conversations[0], {
          tags: selectedTags.map((tag) => ({ name: tag.name })),
        });
        amplitude.logEvent("assign-conversationTag", {
          mode: "single",
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          conversationIds: [conversations[0]._id],
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          channels: [conversations[0].platform],
          tags: selectedTags,
        });
      } else {
        // Remove tags
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        const tagsToRemove = conversations[0].tagIds?.filter(
          (tag) =>
            !selectedTags.some((selectedTag) => selectedTag.name === tag.name),
        );
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        await updateConversation(client, conversations[0], {
          tags: tagsToRemove,
        });
        amplitude.logEvent("remove-conversationTag", {
          mode: "single",
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          conversationIds: [conversations[0]._id],
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          channels: [conversations[0].platform],
          tags: tagsToRemove,
        });
      }
    } else if (conversations.length > 1) {
      const conversationIds = conversations.map(
        (conversation) => conversation._id,
      );
      if (conversationIds.length > 0) {
        const channels = [
          ...new Set(
            conversations.map((conversation) => conversation.platform),
          ),
        ];
        if (mode === "add") {
          await bulkAddTags(client, {
            conversationIds,
            tags: selectedTags.map((tag) => ({ name: tag.name })),
          });
          amplitude.logEvent("assign-conversationTag", {
            mode: "multiple",
            conversationIds,
            channels,
            tags: selectedTags,
          });
        } else {
          // Remove tags
          await bulkRemoveTags(client, {
            conversationIds,
            tags: selectedTags.map((tag) => ({ name: tag.name })),
          });
          amplitude.logEvent("remove-conversationTag", {
            mode: "multiple",
            conversationIds,
            channels,
            tags: selectedTags,
          });
        }
      }
    }
    cleanupFn?.();
    setOpen(false);
  };

  const addTagsToBatch = async (): Promise<{
    batchSize: number;
    done: boolean;
  }> => {
    if (!selectAllInfo) {
      return { batchSize: 0, done: true };
    }
    const { done, value } = await getNextBatch(
      selectAllInfo.bulkActionIterator,
    );
    if (done) {
      return { batchSize: 0, done };
    }
    const conversationIds = value.conversations
      .filter(
        (conversation) =>
          !selectAllInfo.deselectedConversations.some(
            (deselectedConversation) =>
              deselectedConversation._id === conversation._id,
          ),
      )
      .map((conversation) => conversation._id);
    if (conversationIds.length > 0) {
      if (mode === "add") {
        await bulkAddTags(client, {
          conversationIds,
          tags: selectedTags.map((tag) => ({ name: tag.name })),
        });
      } else {
        await bulkRemoveTags(client, {
          conversationIds,
          tags: selectedTags.map((tag) => ({ name: tag.name })),
        });
      }
    }
    return { batchSize: value.conversations.length, done: false };
  };

  // This function synchronously adds tags to each page of conversations.
  // If speed becomes an issue, we could asynchronously add tags to each page.
  const handleAddTagsSelectAll = async () => {
    if (!selectAllInfo) {
      return;
    }
    setUpdatingSelectAll(true);
    selectAllInfo.setBlockRefresh(true);
    let done = false;
    do {
      const result = await addTagsToBatch();
      setNumDone((prevNumDone) => prevNumDone + result.batchSize);
      done = result.done;
    } while (!done);
    if (mode === "add") {
      amplitude.logEvent("assign-conversationTag", {
        mode: "all",
        tags: selectedTags,
      });
    } else {
      amplitude.logEvent("remove-conversationTag", {
        mode: "all",
        tags: selectedTags,
      });
    }
    setUpdatingSelectAll(false);
    selectAllInfo.setBlockRefresh(false);
    cleanupFn?.();
    setOpen(false);
  };

  useEffect(() => {
    if (plural) {
      setHasChanged(selectedTags.length > 0);
    } else {
      setHasChanged(
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        selectedTags.length !== conversations[0].tagIds?.length ||
          selectedTags.some(
            (tag) =>
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              !conversations[0].tagIds?.some(
                (conversationTag) => conversationTag.name === tag.name,
              ),
          ),
      );
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTags]);

  const plural = conversations.length > 1 || !!selectAllInfo;
  const showCreateTagButton = canCreateTags && mode === "add";

  function addTagToConversation(tag: ConversationTagWithId) {
    if (selectedTags.some((usedTag) => usedTag.name === tag.name)) {
      return;
    }

    const tagGroupId = tag.tagGroupId;
    let filteredTags = selectedTags;

    if (tagGroupId && tagGroupsResult.value) {
      const tagGroup = tagGroupsResult.value.find(
        (group) => group._id === tagGroupId,
      );

      if (tagGroup && !tagGroup.allowMultipleTags) {
        filteredTags = selectedTags.filter(
          (existingTag) => existingTag.tagGroupId !== tagGroupId,
        );
      }
    }

    // Eagerly update
    const newTags = [...filteredTags, tag];
    setSelectedTags(newTags);
  }

  function removeTagFromConversation(tag: ConversationTagWithId) {
    // Eagerly update
    const updatedTags = selectedTags.filter(
      (current) => current.name !== tag.name,
    );
    setSelectedTags(updatedTags);
  }

  function onItemSelected({
    item,
    selected,
  }: {
    item: RedoListItem<ConversationTagWithId>;
    selected: boolean;
  }) {
    if (item.type === "section") {
      return;
    }

    if (selected) {
      addTagToConversation(item.value);
    } else {
      removeTagFromConversation(item.value);
    }
  }

  async function handleSubmit({
    name,
    pillTheme,
  }: {
    name: string;
    pillTheme: PillTheme;
  }) {
    await postConversationTag(
      client,
      { name, pillTheme, teamId: team._id },
      reloadConversationTags,
    );
    setCreateTagModalOpen(false);
  }

  const [focusedIndex, setFocusedIndex] = useState<number | undefined>();
  const tagsModal = (
    <RedoModal
      headerBorder={false}
      isOpen={open}
      onModalCloseRequested={() => {
        cleanupFn?.();
        setOpen(false);
      }}
      primaryButton={{
        onClick: async () => {
          if (selectAllInfo) {
            await handleAddTagsSelectAll();
          } else {
            await handleConfirm();
          }
        },
        text: plural
          ? `${mode === "add" ? "Add" : "Remove"} tag${selectedTags.length > 1 ? "s" : ""}`
          : "Confirm",
        disabled: !hasChanged,
      }}
      secondaryButton={{ onClick: () => setOpen(false), text: "Cancel" }}
      title={
        plural
          ? updatingSelectAll
            ? `${mode === "add" ? "Adding tags" : "Removing tags"}`
            : `${mode === "add" ? "Add tags" : "Remove tags"}`
          : "Edit tags"
      }
    >
      {updatingSelectAll ? (
        <div className={tagsModalCss.actionModalContentContainer}>
          <ProgressBar
            value={(numDone / (selectAllInfo?.count || numDone)) * 100}
          />
        </div>
      ) : selectedTagsLoad.pending ? (
        <div className={tagsModalCss.actionModalContentContainer}>
          <Flex align="center" justify="center" style={{ minHeight: "200px" }}>
            <RedoSpinner size="sm" />
          </Flex>
        </div>
      ) : (
        <div className={tagsModalCss.actionModalContentContainer}>
          <Flex dir="column" gap="md">
            <TextInput
              hideFocus
              onChange={setSearchText}
              placeholder={
                !plural
                  ? "Add or remove tags..."
                  : mode === "add"
                    ? "Add tags..."
                    : "Remove tags..."
              }
              ref={setListAnchorRef}
              showBorder={false}
              value={searchText}
            />
            <div className={tagsModalCss.fullWidthDivider}>
              <Divider />
            </div>
            <div className={tagsModalCss.tagsModalContentContainer}>
              <RedoList
                focusedIndex={focusedIndex}
                items={tagListItems}
                itemSelected={onItemSelected}
                refToListenTo={listAnchorRef}
                selectedItems={selectedTagIds}
                selectionVariant="checkbox"
                setFocusedIndex={setFocusedIndex}
              />
              {showCreateTagButton && (
                <RedoButton
                  centerItems={false}
                  className={tagsModalCss.createTagButton}
                  IconLeading={PlusIcon}
                  onClick={() => setCreateTagModalOpen(true)}
                  size="md"
                  text="Create tag"
                />
              )}
            </div>
          </Flex>
        </div>
      )}
    </RedoModal>
  );

  return createTagModalOpen ? (
    <CreateTagModal
      cancelClicked={() => setCreateTagModalOpen(false)}
      handleCreateTag={({
        name,
        pillTheme,
      }: {
        name: string;
        pillTheme: PillTheme;
      }) => handleSubmit({ name, pillTheme })}
      tagsLoad={conversationTagsContext}
    />
  ) : (
    tagsModal
  );
});
